import React from 'react'
import { Theme, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'
import { ICharacter } from '../../../../../../../../api/types/character'
import { useGlobalError } from '../../../../../../../../context/GlobalError'
import { useMutation } from '@tanstack/react-query'
import { mutationSkillRoll } from '../../../../../../../../api/mutations'
import { extractMessage } from '../../../../../../../../utils'
import { SkillItem } from './components/SkillItem/SkillItem'
import { CharacterWeaponProfs } from './components/CharacterWeaponProfs/CharacterWeaponProfs'

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      padding: theme.spacing(1),
      marginLeft: theme.spacing(1),
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    cardsHolder: {
      display: 'flex',
    },
  })
)

interface Props {
  char: ICharacter
  game_id?: number
  showChatTab?: () => void
  editable?: boolean
  onEditSkill?: (name: string, newValue: string) => void
  onEditWeaponProf?: (name: string, newValue: string) => void
}

export const CharacterSkills: React.FC<Props> = ({
  char,
  game_id,
  showChatTab,
  editable,
  onEditSkill,
  onEditWeaponProf,
}) => {
  const classes = useStyles()

  const { addError } = useGlobalError()
  const { mutate: rollMutation, isLoading: isMutating } = useMutation(
    (skill: string) => mutationSkillRoll(char.id, { game_id: game_id || 0, skill_attr: skill }),
    {
      onError: (err: any) => {
        addError(extractMessage(err) || 'Nie udało się wykonać rzutu', err?.status)
      },
      onSuccess: (data: any) => {
        showChatTab && showChatTab()
      },
    }
  )

  const handleRoll = (stat: string) => {
    if (!game_id) {
      return undefined
    }
    return () => rollMutation(stat)
  }

  interface SkillData {
    stat: string
    name: string
    value: number
  }

  const skillsData: SkillData[] = [
    {
      stat: 'acrobatics',
      name: 'Akrobatyka',
      value: char.skills.acrobatics,
    },
    {
      stat: 'alchemy',
      name: 'Alchemia',
      value: char.skills.alchemy,
    },
    {
      stat: 'arcana',
      name: 'Arkana',
      value: char.skills.arcana,
    },
    {
      stat: 'art',
      name: 'Artyzm',
      value: char.skills.art,
    },
    {
      stat: 'athletics',
      name: 'Atletyka',
      value: char.skills.athletics,
    },
    {
      stat: 'investigation',
      name: 'Dochodzenie',
      value: char.skills.investigation,
    },
    {
      stat: 'history',
      name: 'Historia',
      value: char.skills.history,
    },
    {
      stat: 'intuition',
      name: 'Intuicja',
      value: char.skills.intuition,
    },
    {
      stat: 'engineering',
      name: 'Inżynieria',
      value: char.skills.engineering,
    },
    {
      stat: 'hiding',
      name: 'Krycie się',
      value: char.skills.hiding,
    },
    {
      stat: 'legerdemain',
      name: 'Kuglarstwo',
      value: char.skills.legerdemain,
    },
    {
      stat: 'manipulation',
      name: 'Manipulacja',
      value: char.skills.manipulation,
    },
    {
      stat: 'medicine',
      name: 'Medycyna',
      value: char.skills.medicine,
    },
    {
      stat: 'occultism',
      name: 'Okultyzm',
      value: char.skills.occultism,
    },
    {
      stat: 'overawing',
      name: 'Onieśmielenie',
      value: char.skills.overawing,
    },
    {
      stat: 'persuasion',
      name: 'Perswazja',
      value: char.skills.persuasion,
    },
    {
      stat: 'survival',
      name: 'Przetrwanie',
      value: char.skills.survival,
    },
    {
      stat: 'nature',
      name: 'Przyroda',
      value: char.skills.nature,
    },
    {
      stat: 'religion',
      name: 'Religia',
      value: char.skills.religion,
    },
    {
      stat: 'runecraft',
      name: 'Runoznastwo',
      value: char.skills.runecraft,
    },
    {
      stat: 'craft',
      name: 'Rzemiosło',
      value: char.skills.craft,
    },
  ]

  return (
    <div className={classes.cardsHolder}>
      <div className={classes.card}>
        <Typography color="text.secondary" variant="caption" display="block" gutterBottom>
          Umiejętności
        </Typography>
        {skillsData.map(({ stat, name, value }) => (
          <SkillItem
            key={name}
            editable={editable}
            isRolling={isMutating}
            onStatRoll={handleRoll(stat)}
            onEdit={v => onEditSkill && onEditSkill(stat, v)}
            name={name}
            value={value}
          />
        ))}
      </div>
      <CharacterWeaponProfs char={char} editable={editable} onEdit={onEditWeaponProf} />
    </div>
  )
}
